import { useEffect, useState, useRef } from "react";
import { Link } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, Upload, X } from "lucide-react";
import { TextToolbar, FormattingHelp } from "@/lib/booking-form-markdown";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

// Render logged-in previews through the authenticated image route. The stored
// value is the public `/api/public/images/<id>` URL (what the public form
// embeds), but that route only serves once a SAVED row references the image
// (isImageReferencedAsBrandLogo) — a freshly-uploaded, not-yet-saved logo
// would preview as a broken image.
export function appImagePreviewUrl(url: string): string {
  return url.replace("/api/public/images/", "/api/app/images/");
}

export default function AppMembersBookingRequestsSettings() {
  const queryClient = useQueryClient();
  usePageTitle("Booking Request Settings");
  const { toast } = useToast();
  type SettingsResponse = { settings: Record<string, any>; hasCustomSettings: boolean };
  const settingsQuery = useQuery<SettingsResponse>({ queryKey: ["/api/app/booking-requests/settings"] });
  const [form, setForm] = useState<Record<string, any>>({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const descRef = useRef<HTMLTextAreaElement | null>(null);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast({ title: "Only PNG, JPG, WebP, or GIF images are allowed", variant: "destructive" }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: "Logo must be under 5MB", variant: "destructive" }); return; }
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", "/api/app/upload", { filename: file.name, mimeType: file.type, data: base64 });
      const json = await res.json();
      const publicUrl = json.imageUrl.replace("/api/app/images/", "/api/public/images/");
      setForm((f) => ({ ...f, brandLogoUrl: publicUrl }));
      toast({ title: "Logo uploaded" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  }

  function wrapDescription(prefix: string, suffix: string = prefix) {
    const ta = descRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = form.description || "";
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    setForm({ ...form, description: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, end + prefix.length); }, 0);
  }

  function insertListPrefix(marker: string) {
    const ta = descRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const value: string = form.description || "";
    const before = value.slice(0, start);
    const insert = (before.endsWith("\n") || start === 0 ? "" : "\n") + marker;
    const next = before + insert + value.slice(start);
    setForm({ ...form, description: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + insert.length, start + insert.length); }, 0);
  }

  const insertBullets = () => insertListPrefix("- ");
  const insertNumbered = () => insertListPrefix("1. ");

  useEffect(() => {
    if (settingsQuery.data?.settings) setForm(settingsQuery.data.settings);
  }, [settingsQuery.data]);

  const save = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/app/booking-requests/settings", form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/settings"] });
      toast({ title: "Branding saved" });
    },
    onError: (e: any) => toast({ title: "Error", description: parseErrorMessage(e, "Couldn't save branding settings. Please try again."), variant: "destructive" }),
  });

  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl mx-auto">
        <ViewOnlyBanner area="members" />
        <Link href="/app/members/booking-requests">
          <a className="text-sm text-gray-500 hover:underline inline-flex items-center mb-3" data-testid="link-back-bookings"><ChevronLeft className="h-4 w-4" /> Back to Booking Requests</a>
        </Link>
        <div className="flex items-start justify-between gap-4 mb-1">
          <h1 className="text-2xl font-bold" data-testid="text-page-title" data-tour="booking-request-settings-heading">Booking Request Branding</h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="booking-request-settings" />
            <GuideButton pageKey="booking-request-settings" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="booking-request-settings" />
        <p className="text-sm text-gray-500 mb-6">These defaults apply to every booking request form. Individual templates can override them.</p>

        <Card className="p-5 space-y-4" data-tour="booking-request-settings-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Brand name</Label>
              <Input value={form.brandName || ""} onChange={(e) => set("brandName", e.target.value)} placeholder="Your company" data-testid="input-brand-name" />
            </div>
            <div>
              <Label>Accent color</Label>
              <div className="flex gap-2">
                <Input type="color" value={form.accentColor || DEFAULT_ACCENT_COLOR} onChange={(e) => set("accentColor", e.target.value)} className="w-14 p-1" data-testid="input-accent-color" />
                <Input value={form.accentColor || ""} onChange={(e) => set("accentColor", e.target.value)} placeholder={DEFAULT_ACCENT_COLOR} />
              </div>
            </div>
          </div>

          <div>
            <Label>Brand logo</Label>
            <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleLogoUpload} data-testid="input-brand-logo-file" />
            {form.brandLogoUrl ? (
              <div className="flex items-center gap-3 mt-1 border rounded p-2 bg-gray-50">
                <img src={appImagePreviewUrl(form.brandLogoUrl)} alt="Brand logo" className="h-12 w-12 object-contain rounded bg-white border" data-testid="img-brand-logo-preview" />
                <div className="flex-1 text-xs text-gray-600 truncate" data-testid="text-brand-logo-url">{form.brandLogoUrl}</div>
                <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="button-replace-logo">
                  <Upload className="h-3.5 w-3.5 mr-1" /> Replace
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => set("brandLogoUrl", "")} data-testid="button-remove-logo">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="mt-1" data-testid="button-upload-logo">
                <Upload className="h-4 w-4 mr-2" /> {uploadingLogo ? "Uploading…" : "Upload logo"}
              </Button>
            )}
            <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP, or GIF up to 5MB. Leave blank to inherit from your marketing settings.</p>
          </div>

          <div>
            <Label>Headline</Label>
            <Input value={form.headline || ""} onChange={(e) => set("headline", e.target.value)} placeholder="Reserve your room" data-testid="input-headline" />
          </div>

          <div>
            <Label>Description</Label>
            <TextToolbar
              onBold={() => wrapDescription("**")}
              onItalic={() => wrapDescription("_")}
              onUnderline={() => wrapDescription("__")}
              onBullet={insertBullets}
              onNumbered={insertNumbered}
              testId="toolbar-settings-description"
            />
            <Textarea ref={descRef} value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="input-description" placeholder="Welcome! Submit your booking details below." />
            <FormattingHelp testId="formatting-help-settings-description" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Contact phone</Label>
              <Input value={form.contactPhone || ""} onChange={(e) => set("contactPhone", e.target.value)} data-testid="input-contact-phone" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.contactEmail || ""} onChange={(e) => set("contactEmail", e.target.value)} data-testid="input-contact-email" />
            </div>
          </div>

          <div>
            <Label>CTA button text</Label>
            <Input value={form.ctaButtonText || ""} onChange={(e) => set("ctaButtonText", e.target.value)} placeholder="Submit Request" data-testid="input-cta-text" />
          </div>

          <div>
            <Label>Footer text</Label>
            <Input value={form.footerText || ""} onChange={(e) => set("footerText", e.target.value)} data-testid="input-footer-text" />
          </div>

          <div className="border-t pt-4 space-y-2">
            <Label className="text-base">Notifications</Label>
            <div>
              <Label className="text-xs">Notification emails (comma-separated, defaults to your account email)</Label>
              <Input
                value={Array.isArray(form.notifyEmails) ? form.notifyEmails.join(", ") : ""}
                onChange={(e) => set("notifyEmails", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                data-testid="input-notify-emails"
                placeholder="alerts@yourbrand.com, manager@yourbrand.com"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.emailNotificationsEnabled !== false} onCheckedChange={(v) => set("emailNotificationsEnabled", v === true)} data-testid="checkbox-email-notifications" />
              Email me when a new submission arrives
            </label>
            <p className="text-xs text-gray-500">In-app bell notifications are always enabled.</p>
          </div>

          <div className="flex justify-end items-center gap-3 pt-3">
            {uploadingLogo && (
              <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid="text-settings-uploading-hint">
                <Upload className="h-3.5 w-3.5 animate-pulse" />
                Logo is still uploading — please wait before saving.
              </p>
            )}
            <Button onClick={() => save.mutate()} disabled={save.isPending || uploadingLogo} data-testid="button-save-settings">
              {save.isPending ? "Saving…" : uploadingLogo ? "Uploading logo…" : "Save branding"}
            </Button>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
